import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ROPatientContextProvider } from '../PatientSummary/context';
import {
  Stack,
  Typography,
  Divider,
  Button,
  useTheme,
  CustomTheme,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Slide, toast } from 'react-toastify';
import { ROAutocomplete, RODatePicker, ROTextField, ROToggleButtons } from 'shared-components/components/FormFields';

import { getOptionByValue } from '../Careplan/DiagnosisPages/Utils';
import { useQuery } from '@apollo/client';
import { useHistory, useRouteMatch } from 'react-router-dom';
import dayjs from 'op-utils/dayjs';
import { useMutation } from '@apollo/client';
import {
  GET_TREATMENT_REVIEW,
  GET_TREATMENT_REVIEW_LIST,
  REFRESH_DOSE_SITE_SUMMARIES,
  SUBMIT_TREATMENT_REVIEW,
  UPDATE_DOSE_SITE_SUMMARY,
  UPDATE_TREATMENT_REVIEW,
  DISCARD_TREATMENT_REVIEW,
} from './Queries';
import { PatientCard, ROSideNav } from 'op-components';
import { LOAD_CAREPLAN_LIST_DATA } from '../Careplan/ListData/ListQueries';
import { filterListdata } from '../Careplan/ListData/ListData';
import Banner from './Banner';
import { DoseSiteSummaryType, FormStateType } from './Interface';
import PDFPreviewModal from './PDFPreviewModal';
import DoseSiteTable from './DoseSiteTable';
import { BackLink } from 'op-components/SideNav/BackLink';
import ToxicitySymptoms from './ToxicitySymptoms';

const LIST_OPTIONS = {
  RO_PLAN: 'nextStep',
  TREATMENT_TOXICITY_CATEGORY: 'treatmentToxicityCategory',
};

const TREATMENT_TOXICITIES = [
  { label: 'None described', value: 'None described' },
  { label: 'CTCAE v5', value: 'CTCAE v5' },
  { label: 'Other', value: 'Other' },
];
type ValidationStructure = {
  reviewDate: boolean;
  roPlan: boolean;
  toxicityCategories: boolean;
};

const createDebouncedFunction = (func: (...args: any[]) => void, delay: number) => {
  let timer: NodeJS.Timeout | null = null;

  const debouncedFn = (...args: any[]) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
      timer = null;
    }, delay);
  };

  debouncedFn.cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return debouncedFn;
};

const StatusBadge = ({ status }: { status: string }): JSX.Element => {
  const theme = useTheme<CustomTheme>();
  const statusColourMap = {
    'In progress': theme.palette.statusColors.draft,
    'Unsubmitted changes': theme.palette.warning.main,
    Submitted: theme.palette.success.main,
  };
  const statusColor = statusColourMap[status as keyof typeof statusColourMap] || theme.palette.statusColors.draft;
  return (
    <span
      style={{
        backgroundColor: statusColor,
        width: '16px',
        height: '16px',
        borderRadius: '50%',
      }}
    />
  );
};

const TreatmentReviewForm = (): JSX.Element => {
  const theme = useTheme<CustomTheme>();
  const match: { params: { id: string; groupId: string } } = useRouteMatch();
  const { id: patientId, groupId } = match.params;
  const [openPDFViewer, setOpenPDFViewer] = useState(false);
  const { data: listData } = useQuery(LOAD_CAREPLAN_LIST_DATA, {
    fetchPolicy: 'cache-first',
    variables: {
      listCategory: [LIST_OPTIONS.RO_PLAN],
      patientId,
    },
  });

  const {
    loading: reviewLoading,
    data: reviewData,
    refetch: refetchTreatmentReview,
  } = useQuery(GET_TREATMENT_REVIEW, {
    variables: { groupId: groupId },
    fetchPolicy: 'network-only',
  });

  const [updateTreatmentReview] = useMutation(UPDATE_TREATMENT_REVIEW, {
    refetchQueries: [{ query: GET_TREATMENT_REVIEW, variables: { groupId } }],
  });

  const [submitTreatmentReview, { loading: submitLoading }] = useMutation(SUBMIT_TREATMENT_REVIEW, {
    refetchQueries: [
      { query: GET_TREATMENT_REVIEW_LIST, variables: { patientId } },
      { query: GET_TREATMENT_REVIEW, variables: { groupId } },
    ],
    awaitRefetchQueries: true,
  });

  const [refreshDSS, { loading: refreshDSSLoading }] = useMutation(REFRESH_DOSE_SITE_SUMMARIES, {
    refetchQueries: [
      { query: GET_TREATMENT_REVIEW_LIST, variables: { patientId } },
      { query: GET_TREATMENT_REVIEW, variables: { groupId } },
    ],
    awaitRefetchQueries: true,
  });

  const [updateDoseSiteSummary] = useMutation(UPDATE_DOSE_SITE_SUMMARY, {
    refetchQueries: [
      { query: GET_TREATMENT_REVIEW_LIST, variables: { patientId } },
      { query: GET_TREATMENT_REVIEW, variables: { groupId } },
    ],
  });

  const [discardTreatmentReview] = useMutation(DISCARD_TREATMENT_REVIEW);

  const [formState, setFormState] = useState<FormStateType>({
    reviewDate: '',
    roPlan: '',
    doseSiteSummaries: [],
    toxicityType: 'CTCAE v5',
    toxicityCategories: [],
    additionalSummaryReviewNotes: '',
    otherToxicityDetails: '',
  });

  const [isInValidField, setIsInValidField] = React.useState<ValidationStructure>({
    reviewDate: true,
    roPlan: true,
    toxicityCategories: true,
  });

  const [triggerSubmit, setTriggerSubmit] = React.useState<boolean>(false);
  const history = useHistory();
  const [initialLoad, setInitialLoad] = useState(true); // Ref to track initial load
  const [automaticRefreshLoad, setAutomaticRefreshLoad] = useState(false); // Ref to track refresh load automatically triggered when no dose site summaries are found
  const [manualRefreshLoad, setManualRefreshLoad] = useState(false); // Ref to track refresh load manually triggered by the user
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const roPlanOptions = filterListdata(listData, LIST_OPTIONS.RO_PLAN);

  // Scroll to first error field when validation fails on submit
  useEffect(() => {
    if (!triggerSubmit) return;

    // Determine the first invalid field in form order
    const fieldOrder = ['reviewDate', 'roPlan', 'toxicityCategories'] as const;
    const firstInvalidField = fieldOrder.find((field) => isInValidField[field]);

    if (!firstInvalidField) return;

    // Small delay to allow error styling to be applied to the DOM
    const timeoutId = setTimeout(() => {
      // Find the field element by ID or data-testid and scroll to it
      const fieldElement =
        document.getElementById(firstInvalidField) ||
        document.querySelector(`[data-testid="${firstInvalidField}"]`) ||
        document.getElementById(`row-${firstInvalidField}`);

      if (fieldElement) {
        fieldElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [triggerSubmit, isInValidField]);

  useEffect(() => {
    if (reviewData && (initialLoad || automaticRefreshLoad || manualRefreshLoad)) {
      const isSubmitted = Boolean(reviewData.treatmentReview?.submittedAt);
      const hasBeenSubmittedBefore = Boolean(reviewData.treatmentReview?.lastSubmittedAt);
      const isNewDraftReview = !isSubmitted && !hasBeenSubmittedBefore;

      // For new/draft reviews that have never been submitted, use today's local date
      const reviewDate = isNewDraftReview ? dayjs().format('YYYY-MM-DD') : reviewData.treatmentReview?.reviewDate;

      setFormState({
        reviewDate,
        roPlan: reviewData.treatmentReview?.roPlan,
        doseSiteSummaries: reviewData.treatmentReview?.dosesitesummarySet || [],
        toxicityType: reviewData.treatmentReview?.toxicityType,
        toxicityCategories: reviewData.treatmentReview?.toxicityCategories || [],
        practitioner: reviewData.treatmentReview?.practitioner?.name || '',
        additionalSummaryReviewNotes: reviewData.treatmentReview?.additionalSummaryReviewNotes || '',
        otherToxicityDetails: reviewData.treatmentReview?.otherToxicityDetails || '',
      });
      if (reviewData.treatmentReview?.dosesitesummarySet?.length === 0 && initialLoad && !isSubmitted) {
        // only refresh if it is the initial load
        setAutomaticRefreshLoad(true);
        refreshDSS({ variables: { groupId } }).then(() => {
          setAutomaticRefreshLoad(false); // set this so that the form state is reset
        });
      }
      setInitialLoad(false); // Set initial load to false after first load
    }
  }, [reviewData, initialLoad, automaticRefreshLoad, manualRefreshLoad]);

  // Store mutation function in ref so debounced callback always uses latest
  const updateMutationRef = useRef(updateTreatmentReview);
  updateMutationRef.current = updateTreatmentReview;

  const groupIdRef = useRef(groupId);
  groupIdRef.current = groupId;

  // Create debounced function once and store in ref
  const debouncedUpdateRef = useRef(
    createDebouncedFunction((state: FormStateType) => {
      updateMutationRef.current({
        variables: {
          groupId: groupIdRef.current,
          reviewDate: state.reviewDate,
          roPlan: state.roPlan,
          toxicityType: state.toxicityType,
          toxicityCategories: state.toxicityCategories,
          otherToxicityDetails: state.otherToxicityDetails,
          additionalSummaryReviewNotes: state.additionalSummaryReviewNotes,
        },
      });
    }, 100),
  );

  const updateTreatmentReviewCallback = useCallback((newState: FormStateType) => {
    debouncedUpdateRef.current(newState);
  }, []);

  const submitForm = () => {
    // Cancel any pending debounced updates to prevent them from firing after submission
    debouncedUpdateRef.current.cancel();
    setIsSubmitting(true);
    submitTreatmentReview({ variables: { groupId } }).finally(() => {
      setIsSubmitting(false);
    });
  };

  const triggerValidation = () => {
    const formInValid = [
      isInvalid('reviewDate', formState.reviewDate),
      isInvalid('roPlan', formState.roPlan),
      isInvalid('toxicityCategories', formState.toxicityCategories, formState.toxicityType === 'CTCAE v5'),
    ].some((item) => item === true);
    return formInValid;
  };

  const isInvalid = (fieldName: string, value: any, dependentCondition: boolean = true) => {
    if (!dependentCondition) {
      setIsInValidField((prev) => ({ ...prev, [fieldName]: false }));
      return false;
    }
    if (String(value) === '' || value === null) {
      setIsInValidField((prev) => ({ ...prev, [fieldName]: true }));
      return true;
    } else {
      setIsInValidField((prev) => ({ ...prev, [fieldName]: false }));
      return false;
    }
  };

  const handleDiscard = () => {
    discardTreatmentReview({ variables: { groupId } })
      .then((res) => {
        setDiscardModalOpen(false);
        const parentReview = res?.data?.discardTreatmentReview?.review;
        if (parentReview) {
          // Parent exists: stay on page and reload review to show parent as active
          refetchTreatmentReview().then(() => {
            setInitialLoad(true);
            toast.dark(
              <Typography variant="body2" sx={{ color: (theme) => theme.palette.primary.contrastText }}>
                Draft discarded.
              </Typography>,
              {
                position: 'bottom-right',
                autoClose: 3000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                transition: Slide,
                progress: undefined,
                style: { width: 'fit-content' },
              },
            );
          });
        } else {
          history.push(`/radiation/patient/${patientId}/treatment-review`);
        }
      })
      .catch(() => {
        toast.error('Failed to discard draft. Please try again.');
      });
  };

  return (
    <ROPatientContextProvider>
      <Stack direction="row" width={1}>
        <ROSideNav>
          <BackLink url={`/radiation/patient/${patientId}/treatment-review`} title="Treatment review" />
          {/* Treatment review status widget */}
          <Stack padding={2} gap="8px">
            <Typography variant="body1">Treatment Review Status</Typography>
            <Stack direction="row" gap="8px" alignItems="center">
              <StatusBadge status={reviewData?.treatmentReview?.status} />
              <Typography variant="body2" color="text.secondary">
                {reviewData?.treatmentReview?.status}
              </Typography>
            </Stack>
          </Stack>
        </ROSideNav>
        <Stack marginLeft="240px" width={1} sx={{ height: 'calc(100vh - 70px) !important', flexDirection: 'column' }}>
          <Stack sx={{ position: 'relative', zIndex: 2, flexShrink: 0 }}>
            <PatientCard id={patientId} />
          </Stack>
          <Stack sx={{ flexDirection: 'column', display: 'flex', minHeight: 0, height: '100%' }}>
            <Banner
              review={reviewData?.treatmentReview}
              patientId={patientId}
              discardCallback={() => {
                refetchTreatmentReview().then(() => {
                  setInitialLoad(true);
                });
              }}
            />
            <Stack width="100%" sx={{ overflowY: 'auto', flexGrow: 1, paddingBottom: '48px' }}>
              <Stack
                style={{
                  paddingLeft: '24px',
                  paddingTop: '24px',
                  width: '100%',
                  flex: 1,
                  maxWidth: '900px',
                }}>
                <Typography variant="h5" data-testid="treatment-review-form-title">
                  Treatment Review
                </Typography>
                <Typography variant="body1">
                  <span style={{ color: 'red' }}>*</span> = required field
                </Typography>
                <Stack style={{ paddingTop: '24px', width: '100%' }}>
                  <RODatePicker
                    id="reviewDate"
                    fieldlabel="Treatment review date"
                    value={formState.reviewDate ? dayjs(formState.reviewDate) : null}
                    onChange={(date) => {
                      const newDate = dayjs(date).format('YYYY-MM-DD');
                      const dateValue = newDate === 'Invalid Date' ? null : newDate;
                      setFormState((prevValues) => {
                        const newdata = { ...prevValues, reviewDate: dateValue };
                        updateTreatmentReviewCallback(newdata);
                        return newdata;
                      });
                      isInvalid('reviewDate', dateValue);
                    }}
                    required
                    requiredFieldColor="red"
                    error={triggerSubmit && isInValidField.reviewDate}
                  />
                  <ROAutocomplete
                    id="roPlan"
                    fieldlabel="RO Plan"
                    value={getOptionByValue(roPlanOptions, formState.roPlan)}
                    onChange={(value) => {
                      const roPlanValue = typeof value === 'string' ? value : value.value;
                      setFormState((prevValues) => {
                        const newdata = { ...prevValues, roPlan: roPlanValue };
                        updateTreatmentReviewCallback(newdata);
                        return newdata;
                      });
                      isInvalid('roPlan', roPlanValue);
                    }}
                    options={roPlanOptions}
                    required
                    requiredFieldColor="red"
                    fullWidth
                    inputProps={{
                      error: triggerSubmit && isInValidField.roPlan,
                    }}
                  />
                  <DoseSiteTable
                    lastDoseSiteSummariesUpdatedAt={reviewData?.treatmentReview?.doseSiteSummariesLastSyncedAt}
                    doseSiteSummaries={formState.doseSiteSummaries || []}
                    handleDoseSiteSummaryChange={(doseSiteSummary: DoseSiteSummaryType) =>
                      setFormState((prevValues) => {
                        const updatedDoseSiteSummaries = prevValues.doseSiteSummaries.map((dss: DoseSiteSummaryType) =>
                          dss.id === doseSiteSummary.id ? doseSiteSummary : dss,
                        );
                        const newdata = { ...prevValues, doseSiteSummaries: updatedDoseSiteSummaries };
                        updateDoseSiteSummary({
                          variables: { id: doseSiteSummary.id, isSelected: doseSiteSummary.isSelected },
                        });
                        return newdata;
                      })
                    }
                    refreshDSS={() => {
                      setManualRefreshLoad(true);
                      refreshDSS({ variables: { groupId } }).then(() => {
                        setManualRefreshLoad(false);
                      });
                    }}
                    loading={initialLoad || reviewLoading}
                    refreshDSSLoading={refreshDSSLoading}
                    automaticRefreshLoad={automaticRefreshLoad}
                    readOnly={
                      reviewData?.treatmentReview?.status === 'Submitted' ||
                      reviewData?.treatmentReview?.lastSubmittedAt
                    }
                  />
                  <ROToggleButtons
                    id="treatment-toxicities"
                    fieldlabel="Toxicities Assessment"
                    options={TREATMENT_TOXICITIES}
                    value={formState.toxicityType}
                    handleChange={(value) => {
                      setFormState((prevValues) => {
                        const newdata = { ...prevValues, toxicityType: value };
                        updateTreatmentReviewCallback(newdata);
                        return newdata;
                      });
                    }}
                    fullWidth
                  />
                  {formState.toxicityType === 'CTCAE v5' && (
                    <ToxicitySymptoms
                      groupId={groupId}
                      formState={formState}
                      setFormState={setFormState}
                      updateTreatmentReviewCallback={updateTreatmentReviewCallback}
                      isToxicityCategoriesValid={triggerSubmit && isInValidField.toxicityCategories}
                      isInvalid={isInvalid}
                    />
                  )}
                  {formState.toxicityType === 'Other' && (
                    <ROTextField
                      id="other-toxicity-type"
                      fieldlabel="Other: Please specify"
                      value={formState.otherToxicityDetails}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setFormState((prevValues) => ({
                          ...prevValues,
                          otherToxicityDetails: event.target.value,
                        }));
                      }}
                      onBlur={() => {
                        updateTreatmentReviewCallback(formState);
                      }}
                      fullWidth
                    />
                  )}
                  <ROTextField
                    id="additional-summary-review-notes"
                    fieldlabel="Additional summary review notes"
                    value={formState.additionalSummaryReviewNotes}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setFormState((prevValues) => ({
                        ...prevValues,
                        additionalSummaryReviewNotes: event.target.value,
                      }));
                    }}
                    onBlur={() => {
                      updateTreatmentReviewCallback(formState);
                    }}
                    multiline
                    rows={2}
                    fullWidth
                  />
                </Stack>
              </Stack>
            </Stack>
            <Stack sx={{ flexShrink: 0 }}>
              <Divider sx={{ borderColor: (theme) => theme.palette.primary.main }} />

              <Stack flexDirection="row" justifyContent="space-between" paddingBottom={0}>
                <Stack flexDirection="row" justifyContent="space-between" columnGap={1} padding={2} paddingBottom={0}>
                  {reviewData?.treatmentReview?.lastSubmittedAt === null && (
                    <Button
                      id="footer-button-revert"
                      data-testid="footer-button-revert"
                      title="Revert to draft"
                      variant="outlined"
                      onClick={() => {
                        setDiscardModalOpen(true);
                      }}>
                      Discard Draft
                    </Button>
                  )}
                </Stack>

                <Stack flexDirection="row" justifyContent="space-between" columnGap={1} padding={2} paddingBottom={0}>
                  <Button
                    id="footer-button-back"
                    data-testid="footer-button-back"
                    title="Back"
                    variant="text"
                    onClick={() => {
                      history.push(`/radiation/patient/${patientId}/treatment-review`);
                    }}>
                    Back
                  </Button>
                  <Button
                    data-testid="footer-button-preview"
                    variant="outlined"
                    title="Preview"
                    onClick={() => {
                      setOpenPDFViewer(true);
                    }}>
                    Preview PDF
                  </Button>
                  <Button
                    data-testid="footer-button-continue"
                    variant="contained"
                    disabled={submitLoading || reviewData?.treatmentReview?.status == 'Submitted'}
                    sx={{ minWidth: '100px' }}
                    onClick={() => {
                      setTriggerSubmit(true);
                      if (!triggerValidation()) {
                        submitForm();
                      }
                    }}>
                    {isSubmitting ? <CircularProgress size={20} /> : 'Submit'}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
            <PDFPreviewModal
              open={openPDFViewer}
              onClose={() => setOpenPDFViewer(false)}
              url={`/ro_portal/otv/${groupId}/pdf`}
            />

            <Dialog
              open={discardModalOpen}
              onClose={() => {
                setDiscardModalOpen(false);
              }}>
              <DialogTitle>Discard Draft?</DialogTitle>
              <DialogContent>Are you sure you want to discard this treatment review?</DialogContent>
              <DialogActions>
                <Button
                  variant="text"
                  onClick={() => {
                    setDiscardModalOpen(false);
                  }}
                  color="primary">
                  Cancel
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    handleDiscard();
                  }}
                  color="error"
                  sx={{ color: theme.palette.error.main }}>
                  Discard
                </Button>
              </DialogActions>
            </Dialog>
          </Stack>
        </Stack>
      </Stack>
    </ROPatientContextProvider>
  );
};

export default TreatmentReviewForm;
